import { useState, useEffect, useRef, useCallback } from 'react';
import { useStudent } from './useStudent';

type FilterState = {
  center_id?: number;
  course_id?: number;
  batch_id?: number;
  status?: string;
  stage_id?: number;
};

type SortConfig = {
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
};

/** Number of records fetched per lazy chunk */
const LAZY_PAGE_SIZE = 100;

export const useStudentFilters = () => {
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({});
  const [selectedCourseStages, setSelectedCourseStages] = useState<any[]>([]);

  // ── Lazy-load ("All") mode ────────────────────────────────────────────────
  const [isAllMode, setIsAllMode] = useState(false);
  const [lazyPage, setLazyPage] = useState(1);
  const [accumulatedStudents, setAccumulatedStudents] = useState<any[]>([]);
  const [lazyHasMore, setLazyHasMore] = useState(true);

  /**
   * lastProcessedPage — tracks the last page successfully appended.
   *
   * pageRequestedRef — synchronous mutex. Set the instant loadNextLazyPage
   * fires so that even if IntersectionObserver calls it multiple times within
   * the same render cycle (before isFetching turns true), only the first
   * call actually increments the page.  Reset after each page is processed.
   */
  const lastProcessedPage = useRef(0);
  const pageRequestedRef = useRef(false);

  // ── RTK Query ─────────────────────────────────────────────────────────────
  const { getStudentState, paginationData } = useStudent(
    isAllMode
      ? { page: lazyPage, size: LAZY_PAGE_SIZE, search, ...filters, ...sortConfig }
      : { page: pageNumber, size: recordsPerPage, search, ...filters, ...sortConfig }
  );

  const { students: currentPageStudents, isFetching } = getStudentState();

  // ── Accumulation effect ───────────────────────────────────────────────────
  useEffect(() => {
    if (!isAllMode) return;
    if (isFetching) return;                              // wait for request to settle
    if (lazyPage === lastProcessedPage.current) return;  // already handled this page

    // No data → end of results.  Don't stamp the page so a retry can happen
    // if this was a transient empty frame.
    if (currentPageStudents.length === 0) {
      setLazyHasMore(false);
      pageRequestedRef.current = false;
      return;
    }

    // Stamp only after real data arrives.
    lastProcessedPage.current = lazyPage;
    // Release the mutex — ready to accept the next scroll trigger.
    pageRequestedRef.current = false;

    setAccumulatedStudents(prev => {
      if (lazyPage === 1) {
        return currentPageStudents;
      }
      const existingIds = new Set(prev.map((s: any) => s.id));
      const newItems = currentPageStudents.filter((s: any) => !existingIds.has(s.id));
      return newItems.length > 0 ? [...prev, ...newItems] : prev;
    });

    const total = paginationData.totalItems || 0;
    const fetchedSoFar = lazyPage * LAZY_PAGE_SIZE;
    if (currentPageStudents.length < LAZY_PAGE_SIZE || fetchedSoFar >= total) {
      setLazyHasMore(false);
    } else {
      // Automatically fetch next chunk without waiting for scroll
      if (!pageRequestedRef.current) {
        pageRequestedRef.current = true;
        setLazyPage(prev => prev + 1);
      }
    }
  }, [isAllMode, isFetching, lazyPage, currentPageStudents, paginationData.totalItems]);

  // ── Called by IntersectionObserver ────────────────────────────────────────
  // pageRequestedRef guards against multiple firings between React renders.
  const loadNextLazyPage = useCallback(() => {
    if (!isAllMode || !lazyHasMore) return;
    if (pageRequestedRef.current) return; // already requested, wait for it to land
    pageRequestedRef.current = true;
    setLazyPage(prev => prev + 1);
  }, [isAllMode, lazyHasMore]);

  // ── Reset helpers ─────────────────────────────────────────────────────────
  const resetLazyState = useCallback((initialStudents: any[] = []) => {
    setAccumulatedStudents(initialStudents);
    setLazyPage(1);
    lastProcessedPage.current = 0;
    pageRequestedRef.current = false;
    setLazyHasMore(true);
  }, []);

  // ── Public handlers ───────────────────────────────────────────────────────
  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    if (pageSize !== undefined) {
      const total = paginationData.totalItems || 0;
      const isSelectingAll = total > 0 && pageSize >= total;

      if (isSelectingAll) {
        setIsAllMode(true);
        resetLazyState(currentPageStudents || []);
        setRecordsPerPage(pageSize);
        return;
      }

      // Switching back from All → normal pagination
      if (isAllMode) {
        setIsAllMode(false);
        setAccumulatedStudents([]);
        lastProcessedPage.current = 0;
        pageRequestedRef.current = false;
        setLazyHasMore(true);
      }
      setRecordsPerPage(pageSize);
    }
    setPageNumber(page);
  };

  const handleFilterChange = (filterName: string, value: any, courses?: any[]) => {
    setTempFilters(prev => ({
      ...prev,
      [filterName]: filterName === 'status'
        ? value?.value
        : (value?.value ? Number(value.value) : undefined),
    }));

    if (filterName === 'course_id' && courses) {
      const selectedCourse = courses.find(c => c.id.toString() === value?.value);
      setSelectedCourseStages(selectedCourse?.courseStages || []);
      setTempFilters(prev => ({ ...prev, stage_id: undefined }));
    }
  };

  const handleSortChange = (sortBy: string, _order: 'asc' | 'desc') => {
    const newOrder =
      sortConfig.sort_by === sortBy && sortConfig.sort_order === 'asc' ? 'desc' : 'asc';
    setSortConfig({ sort_by: sortBy, sort_order: newOrder });
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  const handleResetFilters = () => {
    const emptyFilters: FilterState = {
      center_id: undefined,
      course_id: undefined,
      batch_id: undefined,
      status: undefined,
      stage_id: undefined,
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setSortConfig({ sort_by: undefined, sort_order: undefined });
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPageNumber(1);
    if (isAllMode) resetLazyState();
  };

  return {
    getStudentState,
    paginationData,
    search,
    pageNumber,
    recordsPerPage,
    filters,
    tempFilters,
    sortConfig,
    handleSearchChange,
    handlePageChange,
    handleFilterChange,
    handleSortChange,
    handleResetFilters,
    handleApplyFilters,
    setTempFilters,
    setRecordsPerPage,
    selectedCourseStages,
    // Lazy load
    isAllMode,
    accumulatedStudents,
    lazyHasMore,
    isLazyLoading: isAllMode && isFetching,
    loadNextLazyPage,
  };
};